import {
	Popover as AriaPopover,
	type PopoverProps as AriaPopoverProps,
	Button,
	Dialog,
	DialogTrigger,
} from 'react-aria-components'
import { cn } from '../lib/cn'

/**
 * Props for the {@link Popover} component.
 *
 * @property children - Trigger element and popover content
 *
 * @example
 * ```tsx
 * <Popover>
 *   <Button>Open</Button>
 *   <PopoverContent>
 *     <div>Popover content</div>
 *   </PopoverContent>
 * </Popover>
 * ```
 */
export interface PopoverProps {
	children: React.ReactNode
}

/**
 * Props for the {@link PopoverContent} component.
 *
 * @property children - Popover content
 * @property className - Optional additional Tailwind classes
 */
export interface PopoverContentProps extends Omit<AriaPopoverProps, 'className'> {
	className?: string
	children: React.ReactNode
}

/**
 * Accessible popover primitive using React Aria Components.
 *
 * @remarks
 * - **Accessibility:** Keyboard navigation (Esc to close), focus management,
 *   ARIA attributes provided by React Aria's `<Popover>` primitive.
 * - **Styling:** All colors and spacing use design tokens from `@atlas/tokens`
 *   (brand). No hardcoded values.
 * - **Trigger:** Shows on click.
 * - **Position:** Auto-positioned by React Aria (top/bottom/left/right).
 * - **Dismissal:** Click outside, Esc key, or programmatic.
 * - **Usage:** Menus, dropdowns, complex tooltips, modal-like overlays.
 *
 * @example Basic usage
 * ```tsx
 * import { Popover, PopoverContent } from '@atlas/ui-primitives'
 * import { Button } from '@atlas/ui-primitives'
 *
 * function MyComponent() {
 *   return (
 *     <Popover>
 *       <Button>Open menu</Button>
 *       <PopoverContent>
 *         <div className="p-4">
 *           <h3>Menu items</h3>
 *           <button>Item 1</button>
 *           <button>Item 2</button>
 *         </div>
 *       </PopoverContent>
 *     </Popover>
 *   )
 * }
 * ```
 *
 * @see packages/ui-primitives/src/popover/popover.stories.tsx for visual examples
 */
export function Popover({ children }: PopoverProps) {
	return <DialogTrigger>{children}</DialogTrigger>
}

/**
 * Popover content component (must be used inside Popover).
 *
 * @remarks
 * - Styled with white background and border
 * - Drop shadow for elevation
 * - Rounded corners
 * - Padding applied by consumer
 *
 * @example
 * ```tsx
 * <Popover>
 *   <Button>Open</Button>
 *   <PopoverContent>
 *     <div className="p-4">Content</div>
 *   </PopoverContent>
 * </Popover>
 * ```
 */
export function PopoverContent({ className, children, ...props }: PopoverContentProps) {
	return (
		<AriaPopover
			{...props}
			offset={8}
			className={cn('bg-ds-background border border-ds-border rounded-ds-md shadow-ds-lg', className)}
		>
			<Dialog className="outline-none">{children}</Dialog>
		</AriaPopover>
	)
}

// Re-export Button for convenience
export { Button as PopoverTrigger }
